import React, { useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const FundingMap = ({ aiData, monthIndex, width, height }) => {
  const [geoData, setGeoData] = useState(null);
  const [nodes, setNodes] = useState([]);

  // Determine if we are on mobile to shrink circles
  const isMobile = width < 600;

  const projection = useMemo(() => {
    // Adjust scale and translate based on width
    const scaleValue = isMobile ? (width / 600) * 150 : 250;
    // We center the map differently for mobile
    const xOffset = isMobile ? width / 2 : width / 1.2;
    const yOffset = isMobile ? height / 1.8 : height / 1.3;

    return d3.geoMercator()
      .scale(scaleValue)
      .translate([xOffset, yOffset]);
  }, [width, height, isMobile]);

  const pathGenerator = d3.geoPath().projection(projection);

  // Make the circles smaller on mobile so they don't cover the whole screen
  const radiusScale = useMemo(() => {
    const maxRadius = isMobile ? 30 : 70;
    return d3.scaleSqrt().domain([0, 60]).range([0, maxRadius]);
  }, [isMobile]);

  // Fetch World Map once
  useEffect(() => {
    d3.json("https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson")
      .then(data => setGeoData(data));
  }, []);

  // Physics Simulation
  useEffect(() => {
    const simulationData = aiData.map(d => {
      const [initialX, initialY] = projection(d.coords);
      return { ...d, x: initialX, y: initialY, targetX: initialX, targetY: initialY };
    });

    const simulation = d3.forceSimulation(simulationData)
      .force("x", d3.forceX(d => d.targetX).strength(0.1))
      .force("y", d3.forceY(d => d.targetY).strength(0.1))
      .force("collide", d3.forceCollide(d => {
        const totalSoFar = d.funding
          .filter(f => f.m <= monthIndex)
          .reduce((sum, f) => sum + f.amt, 0);
        return radiusScale(totalSoFar) + 2;
      }))
      .stop();

    for (let i = 0; i < 120; i++) simulation.tick();
    setNodes([...simulationData]);
  }, [monthIndex, projection, aiData]);


    return (
    <svg
      width={width}
      height={height}
      // viewBox helps the SVG maintain its aspect ratio
      viewBox={`0 0 ${width} ${height}`}
      style={{ background: '#1e293b', borderRadius: '15px', display: 'block', margin: '0 auto' }}
    >
      <g className="map-layer">
        {geoData && geoData.features.map((feature, i) => (
          <path key={`path-${i}`} d={pathGenerator(feature)} fill="#334155" stroke="#475569" strokeWidth="0.5" />
        ))}
      </g>

      <g className="bubble-layer">
        {nodes.map((d) => {
          const totalSoFar = d.funding
            .filter(f => f.m <= monthIndex)
            .reduce((sum, f) => sum + f.amt, 0);
          const r = radiusScale(totalSoFar);
          if (totalSoFar === 0) return null;

          return (
            <g key={d.company} style={{ transition: 'all 0.5s ease-out' }}>
              <circle cx={d.x} cy={d.y} r={r} fill={d.color} fillOpacity="0.7" stroke="white" strokeWidth="1" />
              {r > 1 && (
                <text x={d.x} y={d.y} textAnchor="middle" dy=".3em" fontSize={Math.min(r/1.5 , 12)} fill="white" fontWeight="bold" style={{ pointerEvents: 'none', textShadow: '1px 1px 2px black' }}>
                  {d.company}
                </text>
              )}
            </g>
          );
        })}
      </g>
    </svg>
  );
};

export default FundingMap;
